import { Component, Prop, Host, h, Element, Watch } from '@stencil/core';
import { IconName } from '../icon/icons';

export type OptionValue = {
  /** Value of the option. This is a unique identifier for the option that is not visible to the end user */
  value: string;
  /** A descriptive label for the option. This is what the end user will see. If this field is omitted it will fall back to use the value. */
  label?: string;
  /** An optional extra description for the option. This is rendered below the label */
  description?: string;
};

@Component({
  tag: 'anchor-option',
  styleUrl: 'option.scss',
  shadow: true,
})
export class Option {
  @Element() element: HTMLElement;

  /**
   *  Unique identificator for this DOM node
   */
  @Prop({ reflect: true, mutable: true }) itemId!: string;

  /**
   *  Value of the option. This is a unique identifier for the option that is not visible to the end user
   */
  @Prop({ reflect: true }) value!: string;

  /**
   * A descriptive label for the option. This is what the end user will see. If this field is omitted it will fall back to use the value.
   */
  @Prop({ reflect: true, mutable: true }) label?: string;

  /**
   *  An optional extra description for the option. This is rendered below the label
   */
  @Prop({ reflect: true }) description?: string;

  /**
   *  Value indicating an icon to be shown inside of the option
   */
  @Prop({ reflect: true, mutable: true }) icon?: IconName;

  /**
   * A value indicating whether the option has focus or not, will be used to set a class and apply focused styling.
   */
  @Prop({ reflect: true, mutable: false }) focused?: boolean;

  /**
   * A value indicating whether the option is the selected option, will be used to set a class and apply focused styling.
   */
  @Prop({ reflect: true, mutable: false }) selected?: boolean;

  /**
   *  Value indicating whether or not this particular option should be disabled from selecting
   */
  @Prop({ reflect: true }) disabled: boolean = false;

  /**
   * aria-label for the item
   */
  @Prop({ reflect: true }) ariaLabel: string;

  /** This will set label to the value of *value* in the case where label is unset */
  setLabel(value: string) {
    if (!this.label) {
      this.label = value;
    }
  }

  componentWillLoad() {
    this.setLabel(this.value);
  }

  @Watch('value')
  watchValue(newValue: string) {
    this.setLabel(newValue);
  }

  render() {
    return (
      <Host>
        <div
          id={this.itemId}
          class={{
            'anchor-c-option': true,
            'focused': this.focused,
            'selected': this.selected,
            'disabled': this.disabled,
          }}
          part="option"
          data-value={this.value}
          aria-label={this.ariaLabel}
        >
          <div class="top">
            <span class="label">{this.label ?? <slot />}</span>
            {this.icon && (
              <anchor-icon name={this.icon} size="small" class="icon" color={this.disabled ? 'var(--color-text-disabled)' : 'var(--b-color-neutral-darker)'} stroke={2} />
            )}
          </div>
          <span class="description">{this.description ? this.description : <slot name="description" />}</span>
        </div>
      </Host>
    );
  }
}
